/**
 * Componente GateGuard - CIPA Fácil
 *
 * Componente wrapper que verifica se o usuário pode acessar uma
 * determinada funcionalidade baseado nos gates de fluxo NR-05.
 *
 * Se o acesso for negado, exibe uma mensagem de bloqueio com
 * um botão de ação (CTA) para redirecionar à etapa correta.
 *
 * @author CIPA Fácil Team
 * @date 2026-02-02
 */

import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  AlertTriangle,
  Info,
  ArrowUpCircle,
  ArrowRight,
  Building2,
  Users,
  Calendar,
  FileText,
  ClipboardList,
} from "lucide-react";
import { FlowStep, GateResult } from "../utils/flowGates";
import { useFlowContext } from "../hooks/useFlowContext";
import {
  useTabNavigationOptional,
  TabId,
} from "../context/TabNavigationContext";

/**
 * Props do componente GateGuard
 */
interface GateGuardProps {
  /** Etapa do fluxo que está sendo protegida */
  step: FlowStep;

  /** Conteúdo a ser exibido se o acesso for permitido */
  children: ReactNode;

  /** Título customizado para a mensagem de bloqueio */
  customTitle?: string;

  /** Callback quando o CTA for clicado (opcional) */
  onCtaClick?: (tabOrRoute: string) => void;

  /** Se deve mostrar loading enquanto carrega contexto */
  showLoading?: boolean;

  /** Componente de loading customizado */
  loadingComponent?: ReactNode;

  /** Se deve usar layout inline (sem min-h-screen) */
  inline?: boolean;
}

/**
 * Ícone baseado no tipo de bloqueio
 */
function getBlockIcon(icon?: GateResult["icon"]) {
  switch (icon) {
    case "lock":
      return <Lock className="w-10 h-10 text-gray-600" />;
    case "warning":
      return <AlertTriangle className="w-10 h-10 text-yellow-600" />;
    case "info":
      return <Info className="w-10 h-10 text-blue-600" />;
    case "upgrade":
      return <ArrowUpCircle className="w-10 h-10 text-purple-600" />;
    default:
      return <Lock className="w-10 h-10 text-gray-600" />;
  }
}

/**
 * Ícone baseado na etapa do fluxo
 * @internal Usado para futuras melhorias visuais
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getStepIcon(step: FlowStep) {
  switch (step) {
    case "empresa":
      return <Building2 className="w-6 h-6" />;
    case "mandato":
      return <Calendar className="w-6 h-6" />;
    case "membros":
      return <Users className="w-6 h-6" />;
    case "cronograma":
    case "reunioes":
      return <Calendar className="w-6 h-6" />;
    case "ata":
      return <FileText className="w-6 h-6" />;
    case "relatorios":
    case "documentos":
      return <ClipboardList className="w-6 h-6" />;
    default:
      return <Info className="w-6 h-6" />;
  }
}

/**
 * Cor de fundo baseada no tipo de bloqueio
 */
function getBackgroundColor(icon?: GateResult["icon"]) {
  switch (icon) {
    case "lock":
      return "bg-gray-100";
    case "warning":
      return "bg-yellow-100";
    case "info":
      return "bg-blue-100";
    case "upgrade":
      return "bg-purple-100";
    default:
      return "bg-gray-100";
  }
}

/**
 * Cor do botão CTA baseada no tipo de bloqueio
 */
function getButtonColor(icon?: GateResult["icon"]) {
  switch (icon) {
    case "lock":
      return "bg-gray-600 hover:bg-gray-700";
    case "warning":
      return "bg-yellow-600 hover:bg-yellow-700";
    case "info":
      return "bg-blue-600 hover:bg-blue-700";
    case "upgrade":
      return "bg-purple-600 hover:bg-purple-700";
    default:
      return "bg-blue-600 hover:bg-blue-700";
  }
}

/**
 * Título padrão por tipo de bloqueio
 */
function getDefaultTitle(blockType?: GateResult["blockType"]) {
  switch (blockType) {
    case "flow":
      return "Etapa Anterior Pendente";
    case "plan":
      return "Funcionalidade Bloqueada";
    case "permission":
      return "Acesso Restrito";
    default:
      return "Acesso Bloqueado";
  }
}

/**
 * Componente de mensagem de bloqueio
 */
interface BlockedMessageProps {
  result: GateResult;
  customTitle?: string;
  onCtaClick?: (tabOrRoute: string) => void;
  inline?: boolean;
}

export const BlockedMessage: React.FC<BlockedMessageProps> = ({
  result,
  customTitle,
  onCtaClick,
  inline = false,
}) => {
  const navigate = useNavigate();
  const tabNavigation = useTabNavigationOptional();

  const handleCtaClick = () => {
    // Prioridade: ctaTab (navegação interna) > ctaRoute (navegação externa)
    if (result.ctaTab) {
      if (onCtaClick) {
        onCtaClick(result.ctaTab);
      } else if (tabNavigation) {
        tabNavigation.navigateTo(result.ctaTab as TabId);
      } else {
        console.warn(
          "[GateGuard] TabNavigationContext não disponível para navegar para:",
          result.ctaTab,
        );
      }
    } else if (result.ctaRoute) {
      if (onCtaClick) {
        onCtaClick(result.ctaRoute);
      } else {
        navigate(result.ctaRoute);
      }
    }
  };

  const containerClass = inline
    ? "flex items-center justify-center p-8"
    : "min-h-[60vh] flex items-center justify-center p-4";

  return (
    <div className={containerClass}>
      <div className="text-center max-w-md mx-auto">
        {/* Ícone */}
        <div
          className={`w-20 h-20 ${getBackgroundColor(result.icon)} rounded-full flex items-center justify-center mx-auto mb-6`}
        >
          {getBlockIcon(result.icon)}
        </div>

        {/* Título */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {customTitle || getDefaultTitle(result.blockType)}
        </h2>

        {/* Mensagem */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-gray-700">{result.reason}</p>
        </div>

        {/* Botão CTA */}
        {result.ctaLabel && (result.ctaTab || result.ctaRoute) && (
          <button
            onClick={handleCtaClick}
            className={`${getButtonColor(result.icon)} text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium mx-auto transition-colors`}
          >
            {result.ctaLabel}
            <ArrowRight className="w-5 h-5" />
          </button>
        )}

        {/* Badge de tipo de bloqueio */}
        {result.blockType && (
          <div className="mt-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
              {result.blockType === "flow" && "📋 Fluxo NR-05"}
              {result.blockType === "plan" && "💎 Plano"}
              {result.blockType === "permission" && "🔐 Permissão"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Componente de loading padrão
 */
const DefaultLoading: React.FC<{ inline?: boolean }> = ({ inline }) => {
  const containerClass = inline
    ? "flex items-center justify-center p-8"
    : "min-h-[60vh] flex items-center justify-center";

  return (
    <div className={containerClass}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Verificando permissões...</p>
      </div>
    </div>
  );
};

/**
 * Componente GateGuard
 *
 * Wrapper que verifica se o usuário pode acessar uma funcionalidade
 * baseado nos gates de fluxo NR-05.
 *
 * @example
 * ```tsx
 * // Uso básico
 * <GateGuard step="cronograma">
 *   <CronogramaContent />
 * </GateGuard>
 *
 * // Com título customizado
 * <GateGuard step="ata" customTitle="Ata Digital Bloqueada">
 *   <AtaDigitalContent />
 * </GateGuard>
 *
 * // Inline (sem min-height)
 * <GateGuard step="export" inline>
 *   <ExportButton />
 * </GateGuard>
 * ```
 */
export const GateGuard: React.FC<GateGuardProps> = ({
  step,
  children,
  customTitle,
  onCtaClick,
  showLoading = true,
  loadingComponent,
  inline = false,
}) => {
  const { canAccessStep, loading } = useFlowContext();

  // Mostrar loading enquanto carrega contexto
  if (loading && showLoading) {
    return loadingComponent ? (
      <>{loadingComponent}</>
    ) : (
      <DefaultLoading inline={inline} />
    );
  }

  // Verificar acesso
  const result = canAccessStep(step);

  // Se permitido, renderizar children
  if (result.allowed) {
    return <>{children}</>;
  }

  // Se bloqueado, mostrar mensagem
  return (
    <BlockedMessage
      result={result}
      customTitle={customTitle}
      onCtaClick={onCtaClick}
      inline={inline}
    />
  );
};

/**
 * HOC para proteger componentes com gate
 *
 * @example
 * ```tsx
 * const ProtectedCronograma = withGateGuard('cronograma')(CronogramaComponent);
 * ```
 */
export function withGateGuard<P extends object>(
  step: FlowStep,
  options?: Omit<GateGuardProps, "step" | "children">,
) {
  return function (WrappedComponent: React.ComponentType<P>) {
    return function WithGateGuardWrapper(props: P) {
      return (
        <GateGuard step={step} {...options}>
          <WrappedComponent {...props} />
        </GateGuard>
      );
    };
  };
}

/**
 * Componente para renderizar condicional de export
 */
interface ExportGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ExportGuard: React.FC<ExportGuardProps> = ({
  children,
  fallback,
}) => {
  const { canExport } = useFlowContext();

  if (!canExport) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <BlockedMessage
        result={{
          allowed: false,
          reason:
            "Esta funcionalidade requer um plano superior. Faça upgrade para exportar documentos e relatórios.",
          ctaLabel: "Ver Planos",
          ctaRoute: "/planos",
          icon: "lock",
          blockType: "plan",
        }}
        inline
      />
    );
  }

  return <>{children}</>;
};

export default GateGuard;
